import React from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Shield, LockKeyhole } from 'lucide-react';

interface PermissionGuardProps {
    children: React.ReactNode;
    requiredPermissions?: string[];
    requiredRole?: 'admin' | 'user' | 'magnus' | 'autoGainAdmin' | 'clientAdmin' | 'projectManager' | 'engineer' | 'viewer';
    fallback?: React.ReactNode;
    showMessage?: boolean;
    itemName?: string; // e.g., "Project Management"
}

/**
 * Component that enforces permission checks for User Experience (UX) purposes.
 * 
 * IMPORTANT: This is NOT a security primitive. Frontend checks can be bypassed.
 * Security enforcement MUST be implemented on the backend using 
 * `Depends(require_permission("..."))`.
 * 
 * Always verify that the corresponding backend API endpoint is properly secured.
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
    children,
    requiredPermissions = [],
    requiredRole,
    fallback,
    showMessage = true
}) => {
    const { currentUser } = useAppContext();

    // Check if user is logged in
    if (!currentUser) {
        if (showMessage) {
            return (
                <div className="flex items-center justify-center p-8 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="text-center">
                        <LockKeyhole className="mx-auto mb-2 text-yellow-600" size={32} />
                        <p className="text-yellow-800 font-medium">Please log in to access this content</p>
                    </div>
                </div>
            );
        }
        return fallback ? <>{fallback}</> : null;
    }

    // Check role requirement
    const isAdmin = currentUser.role === 'admin' || currentUser.role === 'autoGainAdmin';
    if (requiredRole && currentUser.role !== requiredRole && !isAdmin) {
        // Special case: clientAdmin can access things required by projectManager, etc.
        const isClientAdmin = currentUser.role === 'clientAdmin';
        const isManager = currentUser.role === 'projectManager';

        let hasAccess = false;
        if (requiredRole === 'engineer') hasAccess = isClientAdmin || isManager || currentUser.role === 'engineer';
        if (requiredRole === 'viewer') hasAccess = true; // Everyone is at least a viewer
        if (requiredRole === 'projectManager') hasAccess = isClientAdmin || isManager;
        if (requiredRole === 'clientAdmin') hasAccess = isClientAdmin;

        if (!hasAccess) {
            if (showMessage) {
                return (
                    <div className="flex items-center justify-center p-12 bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl">
                        <div className="text-center max-w-md">
                            <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-500/30">
                                <Shield className="text-red-500" size={40} />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Private Module</h3>
                            <p className="text-gray-400 mb-8">
                                Your current role (<span className="text-red-400 font-semibold">{currentUser.role}</span>) does not have access to this resource.
                                {requiredRole && <span> Required: <span className="text-blue-400 font-semibold">{requiredRole}</span></span>}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={() => window.location.href = 'mailto:admin@autogain.ai?subject=Access%20Request'}
                                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all transform hover:scale-105 shadow-lg shadow-red-600/20"
                                >
                                    Request Access
                                </button>
                                <button
                                    onClick={() => window.history.back()}
                                    className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl transition-all"
                                >
                                    Go Back
                                </button>
                            </div>
                        </div>
                    </div>
                );
            }
            return fallback ? <>{fallback}</> : null;
        }
    }

    // Check specific permissions
    if (requiredPermissions.length > 0) {
        const hasPermission = requiredPermissions.some(permission =>
            currentUser.permissions.includes('*') || currentUser.permissions.includes(permission)
        );

        if (!hasPermission) {
            if (showMessage) {
                return (
                    <div className="flex items-center justify-center p-8 bg-red-50 border border-red-200 rounded-lg">
                        <div className="text-center">
                            <Shield className="mx-auto mb-2 text-red-600" size={32} />
                            <p className="text-red-800 font-medium">Access Denied</p>
                            <p className="text-sm text-red-600 mt-1">You don&apos;t have permission to view this content</p>
                        </div>
                    </div>
                );
            }
            return fallback ? <>{fallback}</> : null;
        }
    }

    // User has permission
    return <>{children}</>;
};

/**
 * Hook to check if user has specific permission
 */
export const useHasPermission = (permission: string): boolean => {
    const { currentUser } = useAppContext();

    if (!currentUser) return false;
    if (currentUser.permissions.includes('*')) return true;
    return currentUser.permissions.includes(permission);
};

/**
 * Hook to check if user is admin
 */
export const useIsAdmin = (): boolean => {
    const { currentUser } = useAppContext();
    return currentUser?.role === 'admin' || currentUser?.role === 'autoGainAdmin' || currentUser?.role === 'clientAdmin';
};

export default PermissionGuard;
